/**
 * Addresses for the risk-assessment flow ([[BUGS #209]], owner 2026-08-30:
 * *"url with slug variable, not with url params"*).
 *
 * Every mode of this page used to be a query string — `?compose=1`,
 * `?load=<id>&autorun=1`, `?next=rebalance`, `?manage=<id>`, `?source=chat`.
 * Four of those are identities (which portfolio, which mode), and an identity
 * belongs in the path: it is what makes a URL shareable, bookmarkable, and
 * legible in a browser bar.
 *
 * One place builds them and one place reads them, so a caller cannot invent a
 * sixth spelling. **The legacy query forms are still parsed** — a bookmark or
 * an old notification must keep working — but nothing emits them any more.
 */
export const RISK_ASSESSMENT_BASE = '/portfolio/risk-assessment';

export type RiskAssessmentMode =
  | 'hub'        // the reports + portfolios destination
  | 'new'        // a blank holdings table
  | 'edit'       // load a saved book into the editor
  | 'run'        // load it and analyse straight away
  | 'rebalance'  // load, analyse, and land on the rebalance block
  | 'manage'     // mobile: manage one book
  | 'from';      // seeded from elsewhere (watchlist, chat)

export const riskAssessmentPath = {
  hub: () => RISK_ASSESSMENT_BASE,
  new: () => `${RISK_ASSESSMENT_BASE}/new`,
  edit: (portfolioId: string) => `${RISK_ASSESSMENT_BASE}/edit/${encodeURIComponent(portfolioId)}`,
  run: (portfolioId: string) => `${RISK_ASSESSMENT_BASE}/run/${encodeURIComponent(portfolioId)}`,
  rebalance: (portfolioId: string) =>
    `${RISK_ASSESSMENT_BASE}/rebalance/${encodeURIComponent(portfolioId)}`,
  manage: (portfolioId: string) => `${RISK_ASSESSMENT_BASE}/manage/${encodeURIComponent(portfolioId)}`,
  from: (source: string) => `${RISK_ASSESSMENT_BASE}/from/${encodeURIComponent(source)}`,
};

export interface RiskAssessmentRoute {
  mode: RiskAssessmentMode;
  portfolioId: string | null;
  source: string | null;
  /** True for every mode that renders the holdings editor rather than the hub. */
  composing: boolean;
  /** True when the editor should analyse without waiting to be asked. */
  autorun: boolean;
  /** True when the result should land on the rebalance block. */
  toRebalance: boolean;
}

const KNOWN: RiskAssessmentMode[] = ['new', 'edit', 'run', 'rebalance', 'manage', 'from'];

/**
 * Read the route from its path segments, falling back to the legacy query
 * form. `params` is `{ mode, portfolioId }` from `useParams`; `search` is the
 * query string, which is consulted ONLY when the path says nothing — so an old
 * bookmark still lands where it used to.
 */
export function parseRiskAssessmentRoute(
  params: { mode?: string; portfolioId?: string },
  search: URLSearchParams,
): RiskAssessmentRoute {
  const mode = KNOWN.includes(params.mode as RiskAssessmentMode)
    ? (params.mode as RiskAssessmentMode)
    : null;

  if (mode) {
    const id = params.portfolioId ? decodeURIComponent(params.portfolioId) : null;
    return {
      mode,
      portfolioId: mode === 'from' ? null : id,
      source: mode === 'from' ? id : null,
      composing: mode !== 'manage',
      autorun: mode === 'run' || mode === 'rebalance',
      toRebalance: mode === 'rebalance',
    };
  }

  // Legacy query form. Kept readable, never written.
  const legacyLoad = search.get('load');
  const legacySource = search.get('source');
  const legacyManage = search.get('manage');
  const composing = search.has('compose') || Boolean(legacyLoad) || Boolean(legacySource);
  return {
    mode: legacyManage ? 'manage' : composing ? (legacyLoad ? 'edit' : 'new') : 'hub',
    portfolioId: legacyLoad || legacyManage || null,
    source: legacySource,
    composing,
    autorun: search.get('autorun') === '1',
    toRebalance: search.get('next') === 'rebalance',
  };
}
